<template>
  <div class="activity-management">
    <div class="page-header">
      <div>
        <h2 class="page-title">活动管理</h2>
        <p class="page-desc">创建和管理营销活动，提升销量</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        新建活动
      </el-button>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total || 0 }}</div>
        <div class="stat-label">活动总数</div>
      </div>
      <div class="stat-card success">
        <div class="stat-value">{{ stats.active || 0 }}</div>
        <div class="stat-label">进行中</div>
      </div>
    </div>

    <!-- Tab切换 -->
    <el-tabs v-model="activeTab" class="activity-tabs">
      <!-- 主题活动Tab -->
      <el-tab-pane label="主题活动" name="activity">
        <div class="filter-bar">
          <el-input v-model="searchKey" placeholder="搜索活动名称" prefix-icon="Search" clearable style="width: 260px"
            @keyup.enter="fetchActivities" />
          <el-select v-model="activityStatusFilter" placeholder="全部状态" clearable style="width: 120px"
            @change="fetchActivities">
            <el-option label="进行中" :value="1" />
            <el-option label="已结束" :value="0" />
          </el-select>
          <el-button type="primary" @click="fetchActivities">查询</el-button>
          <el-button @click="resetActivitySearch">重置</el-button>
        </div>

        <div class="activity-grid">
          <div class="activity-card" v-for="activity in activityList" :key="activity.id">
            <div class="activity-cover">
              <img :src="activity.cover_image || defaultImage" :alt="activity.activity_name" />
              <div class="activity-status" :class="activity.status === 1 ? 'status-active' : 'status-ended'">
                {{ activity.status === 1 ? '进行中' : '已结束' }}
              </div>
            </div>
            <div class="activity-info">
              <h3 class="activity-name">{{ activity.activity_name }}</h3>
              <div class="activity-type">
                <el-tag size="small" type="info">{{ getActivityTypeText(activity.activity_type) }}</el-tag>
              </div>
              <div class="activity-date">
                <el-icon>
                  <Calendar />
                </el-icon>
                {{ activity.start_date || '未设置' }} ~ {{ activity.end_date || '未设置' }}
              </div>
              <div class="activity-desc">{{ activity.description || '暂无描述' }}</div>
            </div>
            <div class="activity-actions">
              <el-button type="primary" link size="small" @click="openEditDialog(activity)">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
              <el-button type="info" link size="small" @click="viewActivityProducts(activity)">
                <el-icon>
                  <Goods />
                </el-icon> 关联商品
              </el-button>
              <el-popconfirm title="确定删除该活动吗？" @confirm="handleDelete(activity.id, 'activity')">
                <template #reference>
                  <el-button type="danger" link size="small">
                    <el-icon>
                      <Delete />
                    </el-icon> 删除
                  </el-button>
                </template>
              </el-popconfirm>
            </div>
          </div>

          <div v-if="activityList.length === 0 && !activityLoading" class="empty-state">
            <el-empty description="暂无活动，点击右上角新建" />
          </div>
        </div>

        <div class="pagination-wrapper" v-if="activityPagination.total > activityPagination.limit">
          <el-pagination v-model:current-page="activityPagination.page" v-model:page-size="activityPagination.limit"
            :total="activityPagination.total" :page-sizes="[6, 12, 18]" layout="total, sizes, prev, pager, next"
            @size-change="fetchActivities" @current-change="fetchActivities" />
        </div>
      </el-tab-pane>

      <!-- 营销活动Tab -->
      <el-tab-pane label="营销活动" name="promotion">
        <div class="filter-bar">
          <el-select v-model="promotionStatusFilter" placeholder="全部状态" clearable style="width: 120px"
            @change="fetchPromotions">
            <el-option label="进行中" :value="1" />
            <el-option label="未开始" :value="0" />
            <el-option label="已结束" :value="2" />
            <el-option label="已关闭" :value="3" />
          </el-select>
          <el-button type="primary" @click="openAddPromotionDialog">新建营销活动</el-button>
          <el-button @click="fetchPromotions">刷新</el-button>
        </div>

        <el-table :data="promotionList" border v-loading="promotionLoading">
          <el-table-column prop="promotion_name" label="活动名称" min-width="180" />
          <el-table-column label="活动类型" width="120">
            <template #default="{ row }">
              {{ getPromotionTypeText(row.promotion_type) }}
            </template>
          </el-table-column>
          <el-table-column label="活动规则" min-width="200">
            <template #default="{ row }">
              <div class="rule-preview">
                <span v-if="row.promotion_type === 'full_reduction' && row.rule_config?.rules">
                  {{ formatFullReductionText(row.rule_config.rules) }}
                </span>
                <span v-else-if="row.promotion_type === 'group_buy' && row.rule_config">
                  拼团{{ row.rule_config.people_count || 2 }}人，{{ formatDateTime(row.start_time) }}前有效
                </span>
                <span v-else-if="row.promotion_type === 'invite' && row.rule_config">
                  邀请得{{ row.rule_config.invite_points || 0 }}积分
                </span>
                <span v-else-if="row.promotion_type === 'check_in' && row.rule_config">
                  签到得{{ row.rule_config.daily_points || 0 }}积分
                </span>
                <span v-else>-</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="活动时间" width="320">
            <template #default="{ row }">
              {{ formatDateTime(row.start_time) }} ~ {{ formatDateTime(row.end_time) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getPromotionStatusType(row.status)" size="small">
                {{ getPromotionStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180">
            <template #default="{ row }">
              <el-button type="primary" link size="small" @click="openEditPromotionDialog(row)">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
              <el-popconfirm title="确定删除该活动吗？" @confirm="handleDelete(row.id, 'promotion')">
                <template #reference>
                  <el-button type="danger" link size="small">
                    <el-icon>
                      <Delete />
                    </el-icon> 删除
                  </el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper" v-if="promotionPagination.total > promotionPagination.limit">
          <el-pagination v-model:current-page="promotionPagination.page" v-model:page-size="promotionPagination.limit"
            :total="promotionPagination.total" layout="total, prev, pager, next" @current-change="fetchPromotions" />
        </div>
      </el-tab-pane>

      <!-- 秒杀活动Tab -->
      <el-tab-pane label="秒杀活动" name="seckill">
        <div class="filter-bar">
          <el-select v-model="seckillStatusFilter" placeholder="全部状态" clearable style="width: 120px"
            @change="fetchSeckill">
            <el-option label="未开始" :value="0" />
            <el-option label="进行中" :value="1" />
            <el-option label="已结束" :value="2" />
          </el-select>
          <el-button type="primary" @click="openAddSeckillDialog">新建秒杀</el-button>
          <el-button @click="fetchSeckill">刷新</el-button>
        </div>

        <el-table :data="seckillList" border v-loading="seckillLoading">
          <el-table-column label="商品信息" min-width="250">
            <template #default="{ row }">
              <div class="product-info">
                <el-image :src="row.cover_image || defaultImage" fit="cover" class="product-img" />
                <div class="product-detail">
                  <div class="product-name">{{ row.product_name }}</div>
                  <div class="original-price">原价: ¥{{ row.original_price }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="秒杀价" width="100">
            <template #default="{ row }">
              <span class="seckill-price">¥{{ row.seckill_price }}</span>
            </template>
          </el-table-column>
          <el-table-column label="库存/销量" width="140">
            <template #default="{ row }">
              库存: {{ row.seckill_stock }}<br />
              已售: {{ row.sold_count || 0 }}
            </template>
          </el-table-column>
          <el-table-column label="限购" width="80">
            <template #default="{ row }">
              {{ row.user_limit || 1 }}件/人
            </template>
          </el-table-column>
          <el-table-column label="活动时间" width="320">
            <template #default="{ row }">
              {{ formatDateTime(row.start_time) }} ~ {{ formatDateTime(row.end_time) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getSeckillStatusType(row.status)" size="small">
                {{ getSeckillStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180">
            <template #default="{ row }">
              <el-button type="primary" link size="small" @click="openEditSeckillDialog(row)">
                <el-icon>
                  <Edit />
                </el-icon> 编辑
              </el-button>
              <el-popconfirm title="确定删除该秒杀活动吗？" @confirm="handleDelete(row.id, 'seckill')">
                <template #reference>
                  <el-button type="danger" link size="small">
                    <el-icon>
                      <Delete />
                    </el-icon> 删除
                  </el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper" v-if="seckillPagination.total > seckillPagination.limit">
          <el-pagination v-model:current-page="seckillPagination.page" v-model:page-size="seckillPagination.limit"
            :total="seckillPagination.total" layout="total, prev, pager, next" @current-change="fetchSeckill" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 主题活动弹窗 -->
    <el-dialog v-model="activityDialogVisible" :title="isEditActivity ? '编辑活动' : '新建活动'" width="700px" destroy-on-close>
      <el-form :model="activityForm" :rules="activityRules" ref="activityFormRef" label-width="100px">
        <el-form-item label="活动名称" prop="activity_name" required>
          <el-input v-model="activityForm.activity_name" placeholder="请输入活动名称" />
        </el-form-item>

        <el-form-item label="活动类型">
          <el-select v-model="activityForm.activity_type" placeholder="请选择活动类型" clearable style="width: 200px">
            <el-option label="季节性活动" value="seasonal" />
            <el-option label="开业活动" value="opening" />
            <el-option label="节日活动" value="festival" />
            <el-option label="周末活动" value="weekend" />
            <el-option label="会员日" value="member_day" />
          </el-select>
        </el-form-item>

        <el-form-item label="副标题">
          <el-input v-model="activityForm.sub_title" placeholder="请输入副标题" />
        </el-form-item>

        <el-form-item label="活动时间">
          <el-date-picker v-model="activityDateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
            end-placeholder="结束日期" value-format="YYYY-MM-DD" style="width: 100%" />
        </el-form-item>

        <el-form-item label="封面图">
          <el-upload :file-list="activityCoverFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
            :on-change="handleCoverChange" :on-remove="() => activityForm.cover_image = ''" list-type="picture-card">
            <el-icon>
              <Plus />
            </el-icon>
            <template #tip>
              <div class="upload-tip">建议尺寸 800x400px</div>
            </template>
          </el-upload>
        </el-form-item>

        <el-form-item label="背景图">
          <el-upload :file-list="activityBgFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
            :on-change="handleBgChange" :on-remove="() => activityForm.background_image = ''" list-type="picture-card">
            <el-icon>
              <Plus />
            </el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="活动特色">
          <el-select v-model="activityForm.features" multiple filterable allow-create default-first-option
            placeholder="请选择或输入活动特色" style="width: 100%">
            <el-option v-for="item in featureOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>

        <el-form-item label="活动描述">
          <el-input v-model="activityForm.description" type="textarea" :rows="3" placeholder="请输入活动描述" />
        </el-form-item>

        <el-form-item label="关联商品">
          <el-select v-model="activityForm.product_ids" multiple filterable placeholder="请选择关联商品" style="width: 100%">
            <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name"
              :value="product.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="activityForm.sort_order" :min="0" :max="999" />
        </el-form-item>

        <el-form-item label="状态">
          <el-switch v-model="activityForm.status" :active-value="1" :inactive-value="0" active-text="启用"
            inactive-text="禁用" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="activityDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitActivity" :loading="activitySubmitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 营销活动弹窗 - 优化布局版 -->
    <el-dialog v-model="promotionDialogVisible" :title="isEditPromotion ? '编辑营销活动' : '新建营销活动'" width="800px"
      destroy-on-close>
      <el-form :model="promotionForm" :rules="promotionRules" ref="promotionFormRef" label-width="100px">
        <el-form-item label="活动名称" prop="promotion_name" required>
          <el-input v-model="promotionForm.promotion_name" placeholder="请输入活动名称" />
        </el-form-item>

        <el-form-item label="活动类型" prop="promotion_type" required>
          <el-select v-model="promotionForm.promotion_type" placeholder="请选择活动类型" style="width: 100%"
            @change="onPromotionTypeChange">
            <el-option label="满减活动" value="full_reduction" />
            <el-option label="拼团活动" value="group_buy" />
            <el-option label="邀请有礼" value="invite" />
            <el-option label="签到活动" value="check_in" />
          </el-select>
        </el-form-item>

        <el-form-item label="活动时间" required>
          <el-date-picker v-model="promotionTimeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间"
            end-placeholder="结束时间" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <!-- 满减活动规则 -->
        <div v-if="promotionForm.promotion_type === 'full_reduction'" class="rule-section">
          <div class="rule-title">
            <el-icon>
              <Ticket />
            </el-icon>
            满减规则
          </div>
          <div class="full-reduction-rules">
            <div v-for="(rule, index) in fullReductionRules" :key="index" class="rule-item">
              <span class="rule-label">满</span>
              <el-input-number v-model="rule.min_amount" :min="0" :precision="0" :step="10" controls-position="right"
                style="width: 130px" />
              <span class="rule-label">元，减</span>
              <el-input-number v-model="rule.reduce_amount" :min="0" :precision="0" :step="5" controls-position="right"
                style="width: 130px" />
              <span class="rule-label">元</span>
              <el-button type="danger" circle size="small" @click="removeFullReductionRule(index)"
                :disabled="fullReductionRules.length <= 1">
                <el-icon>
                  <Delete />
                </el-icon>
              </el-button>
            </div>
            <el-button type="primary" link @click="addFullReductionRule" class="add-rule-btn">
              <el-icon>
                <Plus />
              </el-icon> 添加阶梯满减
            </el-button>
          </div>
          <div class="rule-tip">💡 提示：满减规则按顺序匹配，只会生效满足条件的第一条</div>
        </div>

        <!-- 拼团活动规则 -->
        <div v-if="promotionForm.promotion_type === 'group_buy'" class="rule-section">
          <div class="rule-title">
            <el-icon>
              <User />
            </el-icon>
            拼团规则
          </div>
          <div class="group-buy-rules">
            <div class="rule-item">
              <span class="rule-label">拼团人数</span>
              <el-input-number v-model="groupBuyRules.people_count" :min="2" :max="100" controls-position="right"
                style="width: 120px" />
              <span class="rule-unit">人</span>
            </div>
            <div class="rule-item">
              <span class="rule-label">拼团时长</span>
              <el-input-number v-model="groupBuyRules.expire_hours" :min="1" :max="72" controls-position="right"
                style="width: 120px" />
              <span class="rule-unit">小时</span>
            </div>
            <div class="rule-item">
              <span class="rule-label">拼团价</span>
              <el-input-number v-model="groupBuyRules.group_price" :min="0" :precision="2" :step="10"
                controls-position="right" style="width: 140px" />
              <span class="rule-unit">元</span>
            </div>
          </div>
          <div class="rule-tip">💡 提示：拼团价会覆盖商品原售价</div>
        </div>

        <!-- 邀请有礼规则 -->
        <div v-if="promotionForm.promotion_type === 'invite'" class="rule-section">
          <div class="rule-title">
            <el-icon>
              <Share />
            </el-icon>
            邀请规则
          </div>

          <div class="reward-group">
            <div class="reward-group-title">📊 积分奖励</div>
            <div class="reward-row">
              <div class="reward-item">
                <span class="reward-label">邀请人</span>
                <el-input-number v-model="inviteRules.invite_points" :min="0" :step="10" controls-position="right"
                  style="width: 130px" />
                <span class="reward-unit">积分</span>
              </div>
              <div class="reward-item">
                <span class="reward-label">被邀请人</span>
                <el-input-number v-model="inviteRules.invitee_points" :min="0" :step="10" controls-position="right"
                  style="width: 130px" />
                <span class="reward-unit">积分</span>
              </div>
            </div>
          </div>

          <div class="reward-group">
            <div class="reward-group-title">🎫 优惠券奖励</div>
            <div class="reward-row">
              <div class="reward-item reward-item-select">
                <span class="reward-label">邀请人</span>
                <el-select v-model="inviteRules.invite_coupon_id" placeholder="请选择优惠券" clearable style="width: 180px">
                  <el-option v-for="coupon in couponOptions" :key="coupon.id" :label="coupon.coupon_name"
                    :value="coupon.id" />
                </el-select>
              </div>
              <div class="reward-item reward-item-select">
                <span class="reward-label">被邀请人</span>
                <el-select v-model="inviteRules.invitee_coupon_id" placeholder="请选择优惠券" clearable style="width: 180px">
                  <el-option v-for="coupon in couponOptions" :key="coupon.id" :label="coupon.coupon_name"
                    :value="coupon.id" />
                </el-select>
              </div>
            </div>
          </div>
        </div>

        <!-- 签到活动规则 -->
        <div v-if="promotionForm.promotion_type === 'check_in'" class="rule-section">
          <div class="rule-title">
            <el-icon>
              <Calendar />
            </el-icon>
            签到规则
          </div>

          <div class="reward-group">
            <div class="reward-group-title">📅 每日签到</div>
            <div class="rule-item">
              <span class="rule-label">每日签到积分</span>
              <el-input-number v-model="checkInRules.daily_points" :min="0" :step="1" controls-position="right"
                style="width: 120px" />
              <span class="rule-unit">积分</span>
            </div>
          </div>

          <div class="reward-group">
            <div class="reward-group-title">🔥 连续签到奖励</div>
            <div class="continuous-rewards">
              <div v-for="(reward, index) in checkInRules.continuous_rewards" :key="index" class="rule-item">
                <span class="rule-label">连续</span>
                <el-input-number v-model="reward.days" :min="2" :max="30" controls-position="right"
                  style="width: 90px" />
                <span class="rule-label">天，奖励</span>
                <el-input-number v-model="reward.points" :min="0" :step="10" controls-position="right"
                  style="width: 110px" />
                <span class="rule-label">积分</span>
                <el-button type="danger" circle size="small" @click="removeContinuousReward(index)">
                  <el-icon>
                    <Delete />
                  </el-icon>
                </el-button>
              </div>
              <el-button type="primary" link @click="addContinuousReward" class="add-rule-btn">
                <el-icon>
                  <Plus />
                </el-icon> 添加连续签到奖励
              </el-button>
            </div>
          </div>
          <div class="rule-tip">💡 提示：连续签到奖励会与每日签到积分叠加发放</div>
        </div>

        <el-form-item label="适用商品">
          <el-select v-model="promotionForm.applicable_products_list" multiple filterable placeholder="请选择适用商品，不选表示全部商品"
            style="width: 100%">
            <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name"
              :value="product.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="适用用户">
          <el-select v-model="promotionForm.applicable_users_list" multiple placeholder="请选择适用用户等级，不选表示全部用户"
            style="width: 100%">
            <el-option label="普通会员" value="bronze" />
            <el-option label="银卡会员" value="silver" />
            <el-option label="金卡会员" value="gold" />
            <el-option label="白金会员" value="platinum" />
            <el-option label="钻石会员" value="diamond" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-select v-model="promotionForm.status" placeholder="请选择状态" style="width: 100%">
            <el-option label="未开始" :value="0" />
            <el-option label="进行中" :value="1" />
            <el-option label="已结束" :value="2" />
            <el-option label="已关闭" :value="3" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="promotionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPromotion" :loading="promotionSubmitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 秒杀活动弹窗 -->
    <el-dialog v-model="seckillDialogVisible" :title="isEditSeckill ? '编辑秒杀活动' : '新建秒杀活动'" width="600px"
      destroy-on-close>
      <el-form :model="seckillForm" :rules="seckillRules" ref="seckillFormRef" label-width="100px">
        <el-form-item label="选择商品" prop="product_id" required>
          <el-select v-model="seckillForm.product_id" placeholder="请选择商品" filterable style="width: 100%">
            <el-option v-for="product in productOptions" :key="product.id" :label="product.product_name"
              :value="product.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="秒杀价" prop="seckill_price" required>
          <el-input-number v-model="seckillForm.seckill_price" :min="0" :precision="2" :step="10" style="width: 100%" />
        </el-form-item>

        <el-form-item label="秒杀库存" prop="seckill_stock" required>
          <el-input-number v-model="seckillForm.seckill_stock" :min="1" :step="10" style="width: 100%" />
        </el-form-item>

        <el-form-item label="每人限购">
          <el-input-number v-model="seckillForm.user_limit" :min="1" :max="10" style="width: 100%" />
        </el-form-item>

        <!-- 修改后 - 移除 prop="start_time" -->
        <el-form-item label="活动时间" required>
          <el-date-picker v-model="seckillTimeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间"
            end-placeholder="结束时间" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <el-form-item label="状态">
          <el-select v-model="seckillForm.status" placeholder="请选择状态" style="width: 100%">
            <el-option label="未开始" :value="0" />
            <el-option label="进行中" :value="1" />
            <el-option label="已结束" :value="2" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="seckillDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSeckill" :loading="seckillSubmitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 关联商品弹窗 -->
    <el-dialog v-model="productDialogVisible" :title="`${currentActivity?.activity_name} - 关联商品`" width="700px">
      <el-table :data="activityProducts" border>
        <el-table-column label="商品图片" width="80">
          <template #default="{ row }">
            <el-image :src="row.cover_image || defaultImage" fit="cover"
              style="width: 50px; height: 50px; border-radius: 4px;" />
          </template>
        </el-table-column>
        <el-table-column prop="product_name" label="商品名称" min-width="200" />
        <el-table-column prop="sale_price" label="售价" width="100">
          <template #default="{ row }">¥{{ row.sale_price }}</template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="80" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { Plus, Edit, Delete, Calendar, Goods, Ticket, User, Share } from '@element-plus/icons-vue'

export default {
  name: 'ActivityManagementPage',
  components: { Plus, Edit, Delete, Calendar, Goods, Ticket, User, Share },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)
    const defaultImage = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

    // Tab切换
    const activeTab = ref('activity')

    // 主题活动相关
    const activityLoading = ref(false)
    const activityList = ref([])
    const stats = ref({})
    const activitySearchKey = ref('')
    const activityStatusFilter = ref(null)
    const activityPagination = reactive({ page: 1, limit: 6, total: 0 })

    // 营销活动相关
    const promotionLoading = ref(false)
    const promotionList = ref([])
    const promotionStatusFilter = ref(null)
    const promotionPagination = reactive({ page: 1, limit: 10, total: 0 })

    // 秒杀活动相关
    const seckillLoading = ref(false)
    const seckillList = ref([])
    const seckillStatusFilter = ref(null)
    const seckillPagination = reactive({ page: 1, limit: 10, total: 0 })

    // 商品选项
    const productOptions = ref([])
    // 优惠券选项（用于邀请有礼）
    const couponOptions = ref([])

    // 特色选项
    const featureOptions = ['香椿', '柳芽', '花椒芽', '全场折扣', '满减优惠', '赠品', '积分翻倍']

    // 主题活动弹窗
    const activityDialogVisible = ref(false)
    const isEditActivity = ref(false)
    const activitySubmitting = ref(false)
    const activityFormRef = ref(null)
    const activityDateRange = ref([])
    const activityCoverFiles = ref([])
    const activityBgFiles = ref([])

    const activityForm = reactive({
      id: null,
      activity_name: '',
      activity_type: '',
      sub_title: '',
      cover_image: '',
      background_image: '',
      description: '',
      features: [],
      start_date: '',
      end_date: '',
      product_ids: [],
      status: 1,
      sort_order: 0
    })

    const activityRules = {
      activity_name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }]
    }

    // 营销活动弹窗
    const promotionDialogVisible = ref(false)
    const isEditPromotion = ref(false)
    const promotionSubmitting = ref(false)
    const promotionFormRef = ref(null)
    const promotionTimeRange = ref([])

    const promotionForm = reactive({
      id: null,
      promotion_name: '',
      promotion_type: '',
      start_time: '',
      end_time: '',
      applicable_products_list: [],
      applicable_users_list: [],
      status: 0
    })

    // 修改后 - 完全移除时间验证
    const promotionRules = {
      promotion_name: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
      promotion_type: [{ required: true, message: '请选择活动类型', trigger: 'change' }]
    }
    // 满减规则
    const fullReductionRules = ref([
      { min_amount: 100, reduce_amount: 10 }
    ])

    // 拼团规则
    const groupBuyRules = reactive({
      people_count: 2,
      expire_hours: 24,
      group_price: 0
    })

    // 邀请规则
    const inviteRules = reactive({
      invite_points: 0,
      invitee_points: 0,
      invite_coupon_id: null,
      invitee_coupon_id: null
    })

    // 签到规则
    const checkInRules = reactive({
      daily_points: 5,
      continuous_rewards: []
    })

    // 秒杀活动弹窗
    const seckillDialogVisible = ref(false)
    const isEditSeckill = ref(false)
    const seckillSubmitting = ref(false)
    const seckillFormRef = ref(null)
    const seckillTimeRange = ref([])

    const seckillForm = reactive({
      id: null,
      product_id: null,
      seckill_price: 0,
      seckill_stock: 0,
      user_limit: 1,
      start_time: '',
      end_time: '',
      status: 0
    })

    const seckillRules = {
      product_id: [{ required: true, message: '请选择商品', trigger: 'change' }],
      seckill_price: [{ required: true, message: '请输入秒杀价', trigger: 'blur' }],
      seckill_stock: [{ required: true, message: '请输入秒杀库存', trigger: 'blur' }]
    }

    // 关联商品弹窗
    const productDialogVisible = ref(false)
    const currentActivity = ref(null)
    const activityProducts = ref([])

    // 获取活动类型文本
    const getActivityTypeText = (type) => {
      const map = { seasonal: '季节性', opening: '开业', festival: '节日', weekend: '周末', member_day: '会员日' }
      return map[type] || type || '普通活动'
    }

    // 获取营销活动类型文本
    const getPromotionTypeText = (type) => {
      const map = { full_reduction: '满减', group_buy: '拼团', invite: '邀请有礼', check_in: '签到' }
      return map[type] || type
    }

    const getPromotionStatusText = (status) => {
      const map = { 0: '未开始', 1: '进行中', 2: '已结束', 3: '已关闭' }
      return map[status] || '未知'
    }

    const getPromotionStatusType = (status) => {
      const map = { 0: 'info', 1: 'success', 2: 'danger', 3: 'danger' }
      return map[status] || 'info'
    }

    const getSeckillStatusText = (status) => {
      const map = { 0: '未开始', 1: '进行中', 2: '已结束' }
      return map[status] || '未知'
    }

    const getSeckillStatusType = (status) => {
      const map = { 0: 'info', 1: 'success', 2: 'danger' }
      return map[status] || 'info'
    }

    const formatDateTime = (time) => {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }

    // 格式化满减规则文本
    const formatFullReductionText = (rules) => {
      if (!rules || rules.length === 0) return '-'
      return rules.map(r => `满${r.min_amount}减${r.reduce_amount}`).join('，')
    }

    // 获取商品列表
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          productOptions.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取商品失败:', error)
      }
    }

    // 获取优惠券列表
    const fetchCoupons = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/coupon_api.php`, {
          params: { action: 'list', agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          couponOptions.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取优惠券失败:', error)
      }
    }

    // 获取主题活动列表
    const fetchActivities = async () => {
      activityLoading.value = true
      try {
        const params = {
          action: 'activities',
          agent_id: agentId.value,
          store_id: storeId.value,
          page: activityPagination.page,
          limit: activityPagination.limit,
          search: activitySearchKey.value,
          status: activityStatusFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, { params })

        if (response.data.code === 200) {
          activityList.value = response.data.data.list.map(item => ({
            ...item,
            features: item.features ? (typeof item.features === 'string' ? JSON.parse(item.features) : item.features) : []
          }))
          stats.value = response.data.data.stats
          activityPagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取活动失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        activityLoading.value = false
      }
    }

    // 获取营销活动列表
    const fetchPromotions = async () => {
      promotionLoading.value = true
      try {
        const params = {
          action: 'promotions',
          agent_id: agentId.value,
          store_id: storeId.value,
          page: promotionPagination.page,
          limit: promotionPagination.limit,
          status: promotionStatusFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, { params })

        if (response.data.code === 200) {
          promotionList.value = response.data.data.list.map(item => ({
            ...item,
            rule_config: item.rule_config ? (typeof item.rule_config === 'string' ? JSON.parse(item.rule_config) : item.rule_config) : {}
          }))
          promotionPagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取营销活动失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        promotionLoading.value = false
      }
    }

    // 获取秒杀活动列表
    const fetchSeckill = async () => {
      seckillLoading.value = true
      try {
        const params = {
          action: 'seckill',
          agent_id: agentId.value,
          store_id: storeId.value,
          page: seckillPagination.page,
          limit: seckillPagination.limit,
          status: seckillStatusFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, { params })

        if (response.data.code === 200) {
          seckillList.value = response.data.data.list
          seckillPagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取秒杀活动失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        seckillLoading.value = false
      }
    }

    // 重置活动搜索
    const resetActivitySearch = () => {
      activitySearchKey.value = ''
      activityStatusFilter.value = null
      activityPagination.page = 1
      fetchActivities()
    }

    // 活动类型切换时重置规则
    const onPromotionTypeChange = () => {
      // 重置各种规则
      fullReductionRules.value = [{ min_amount: 100, reduce_amount: 10 }]
      groupBuyRules.people_count = 2
      groupBuyRules.expire_hours = 24
      groupBuyRules.group_price = 0
      inviteRules.invite_points = 0
      inviteRules.invitee_points = 0
      inviteRules.invite_coupon_id = null
      inviteRules.invitee_coupon_id = null
      checkInRules.daily_points = 5
      checkInRules.continuous_rewards = []
    }

    // 满减规则操作
    const addFullReductionRule = () => {
      fullReductionRules.value.push({ min_amount: 0, reduce_amount: 0 })
    }

    const removeFullReductionRule = (index) => {
      fullReductionRules.value.splice(index, 1)
    }

    // 连续签到奖励操作
    const addContinuousReward = () => {
      checkInRules.continuous_rewards.push({ days: 7, points: 20 })
    }

    const removeContinuousReward = (index) => {
      checkInRules.continuous_rewards.splice(index, 1)
    }

    // 构建规则配置JSON
    const buildRuleConfig = () => {
      const type = promotionForm.promotion_type

      if (type === 'full_reduction') {
        return { rules: fullReductionRules.value }
      } else if (type === 'group_buy') {
        return {
          people_count: groupBuyRules.people_count,
          expire_hours: groupBuyRules.expire_hours,
          group_price: groupBuyRules.group_price
        }
      } else if (type === 'invite') {
        return {
          invite_points: inviteRules.invite_points,
          invitee_points: inviteRules.invitee_points,
          invite_coupon_id: inviteRules.invite_coupon_id,
          invitee_coupon_id: inviteRules.invitee_coupon_id
        }
      } else if (type === 'check_in') {
        return {
          daily_points: checkInRules.daily_points,
          continuous_rewards: checkInRules.continuous_rewards
        }
      }

      return {}
    }

    // 解析规则配置JSON到表单
    const parseRuleConfig = (ruleConfig) => {
      const type = promotionForm.promotion_type
      if (!ruleConfig) return

      if (type === 'full_reduction' && ruleConfig.rules) {
        fullReductionRules.value = ruleConfig.rules
      } else if (type === 'group_buy') {
        groupBuyRules.people_count = ruleConfig.people_count || 2
        groupBuyRules.expire_hours = ruleConfig.expire_hours || 24
        groupBuyRules.group_price = ruleConfig.group_price || 0
      } else if (type === 'invite') {
        inviteRules.invite_points = ruleConfig.invite_points || 0
        inviteRules.invitee_points = ruleConfig.invitee_points || 0
        inviteRules.invite_coupon_id = ruleConfig.invite_coupon_id || null
        inviteRules.invitee_coupon_id = ruleConfig.invitee_coupon_id || null
      } else if (type === 'check_in') {
        checkInRules.daily_points = ruleConfig.daily_points || 5
        checkInRules.continuous_rewards = ruleConfig.continuous_rewards || []
      }
    }

    // 上传图片
    const uploadImage = async (file) => {
      if (!file.type.startsWith('image/')) {
        ElMessage.error('只能上传图片文件')
        return null
      }
      if (file.size / 1024 / 1024 > 5) {
        ElMessage.error('图片大小不能超过 5MB')
        return null
      }

      try {
        const signature = await getUploadSignature('image')
        const ossRes = await uploadToOSS(file, signature)
        return ossRes.url
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error('上传失败')
        return null
      }
    }

    const handleCoverChange = async (file) => {
      if (!file.raw) return
      const url = await uploadImage(file.raw)
      if (url) {
        activityForm.cover_image = url
        activityCoverFiles.value = [{ uid: file.uid, name: file.name, url }]
      }
    }

    const handleBgChange = async (file) => {
      if (!file.raw) return
      const url = await uploadImage(file.raw)
      if (url) {
        activityForm.background_image = url
        activityBgFiles.value = [{ uid: file.uid, name: file.name, url }]
      }
    }

    // 打开新增活动弹窗
    const openAddDialog = () => {
      isEditActivity.value = false
      activityDateRange.value = []
      activityCoverFiles.value = []
      activityBgFiles.value = []
      Object.assign(activityForm, {
        id: null,
        activity_name: '',
        activity_type: '',
        sub_title: '',
        cover_image: '',
        background_image: '',
        description: '',
        features: [],
        start_date: '',
        end_date: '',
        product_ids: [],
        status: 1,
        sort_order: 0
      })
      activityDialogVisible.value = true
    }

    // 打开编辑活动弹窗
    const openEditDialog = (activity) => {
      isEditActivity.value = true
      Object.assign(activityForm, activity)

      if (activity.start_date && activity.end_date) {
        activityDateRange.value = [activity.start_date, activity.end_date]
      }

      if (activity.cover_image) {
        activityCoverFiles.value = [{ uid: Date.now(), name: 'cover', url: activity.cover_image }]
      }
      if (activity.background_image) {
        activityBgFiles.value = [{ uid: Date.now(), name: 'bg', url: activity.background_image }]
      }

      activityDialogVisible.value = true
    }

    // 提交活动
    const submitActivity = async () => {
      if (!activityFormRef.value) return

      await activityFormRef.value.validate(async (valid) => {
        if (!valid) return

        activitySubmitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            type: 'activity',
            agent_id: agentId.value,
            store_id: storeId.value,
            ...activityForm
          }

          if (activityDateRange.value && activityDateRange.value.length === 2) {
            payload.start_date = activityDateRange.value[0]
            payload.end_date = activityDateRange.value[1]
          }

          payload.features = JSON.stringify(payload.features)

          let response
          if (isEditActivity.value) {
            payload.id = activityForm.id
            response = await axios.put(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditActivity.value ? '更新成功' : '创建成功')
            activityDialogVisible.value = false
            fetchActivities()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败')
        } finally {
          activitySubmitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 打开新增营销活动弹窗
    const openAddPromotionDialog = () => {
      isEditPromotion.value = false
      promotionTimeRange.value = []  // 初始化为空数组

      // 重置规则
      fullReductionRules.value = [{ min_amount: 100, reduce_amount: 10 }]
      groupBuyRules.people_count = 2
      groupBuyRules.expire_hours = 24
      groupBuyRules.group_price = 0
      inviteRules.invite_points = 0
      inviteRules.invitee_points = 0
      inviteRules.invite_coupon_id = null
      inviteRules.invitee_coupon_id = null
      checkInRules.daily_points = 5
      checkInRules.continuous_rewards = []

      Object.assign(promotionForm, {
        id: null,
        promotion_name: '',
        promotion_type: '',
        start_time: '',
        end_time: '',
        applicable_products_list: [],
        applicable_users_list: [],
        status: 0
      })

      promotionDialogVisible.value = true
    }

    // 打开编辑营销活动弹窗
    const openEditPromotionDialog = (promotion) => {
      isEditPromotion.value = true
      Object.assign(promotionForm, promotion)

      // 解析适用商品和用户
      if (promotion.applicable_products) {
        promotionForm.applicable_products_list = promotion.applicable_products.split(',').map(v => parseInt(v)).filter(v => v)
      } else {
        promotionForm.applicable_products_list = []
      }

      if (promotion.applicable_users) {
        promotionForm.applicable_users_list = promotion.applicable_users.split(',')
      } else {
        promotionForm.applicable_users_list = []
      }

      // 解析规则配置
      if (promotion.rule_config) {
        parseRuleConfig(promotion.rule_config)
      }

      // 设置时间范围
      if (promotion.start_time && promotion.end_time) {
        promotionTimeRange.value = [promotion.start_time, promotion.end_time]
      } else {
        promotionTimeRange.value = []
      }

      promotionDialogVisible.value = true
    }

    // 提交营销活动
    const submitPromotion = async () => {
      if (!promotionFormRef.value) return

      // 先手动验证时间是否已选择
      if (!promotionTimeRange.value || promotionTimeRange.value.length !== 2) {
        ElMessage.warning('请选择活动时间')
        return
      }

      await promotionFormRef.value.validate(async (valid) => {
        if (!valid) return

        promotionSubmitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            type: 'promotion',
            agent_id: agentId.value,
            store_id: storeId.value,
            promotion_name: promotionForm.promotion_name,
            promotion_type: promotionForm.promotion_type,
            start_time: promotionTimeRange.value[0],  // 明确赋值
            end_time: promotionTimeRange.value[1],    // 明确赋值
            status: promotionForm.status
          }

          // 调试日志，检查时间值
          console.log('活动时间:', promotionTimeRange.value)
          console.log('start_time:', payload.start_time)
          console.log('end_time:', payload.end_time)

          // 构建规则配置
          payload.rule_config = buildRuleConfig()

          // 处理适用商品
          if (promotionForm.applicable_products_list && promotionForm.applicable_products_list.length > 0) {
            payload.applicable_products = promotionForm.applicable_products_list.join(',')
          } else {
            payload.applicable_products = null
          }

          // 处理适用用户
          if (promotionForm.applicable_users_list && promotionForm.applicable_users_list.length > 0) {
            payload.applicable_users = promotionForm.applicable_users_list.join(',')
          } else {
            payload.applicable_users = null
          }

          let response
          if (isEditPromotion.value) {
            payload.id = promotionForm.id
            response = await axios.put(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditPromotion.value ? '更新成功' : '创建成功')
            promotionDialogVisible.value = false
            fetchPromotions()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败')
        } finally {
          promotionSubmitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 打开新增秒杀弹窗
    const openAddSeckillDialog = () => {
      isEditSeckill.value = false
      seckillTimeRange.value = []
      Object.assign(seckillForm, {
        id: null,
        product_id: null,
        seckill_price: 0,
        seckill_stock: 0,
        user_limit: 1,
        start_time: '',
        end_time: '',
        status: 0
      })
      seckillDialogVisible.value = true
    }

    // 打开编辑秒杀弹窗
    const openEditSeckillDialog = (seckill) => {
      isEditSeckill.value = true
      Object.assign(seckillForm, seckill)

      if (seckill.start_time && seckill.end_time) {
        seckillTimeRange.value = [seckill.start_time, seckill.end_time]
      }

      seckillDialogVisible.value = true
    }

    // 提交秒杀活动
    const submitSeckill = async () => {
      if (!seckillFormRef.value) return

      // 先手动验证时间是否已选择
      if (!seckillTimeRange.value || seckillTimeRange.value.length !== 2) {
        ElMessage.warning('请选择活动时间')
        return
      }

      await seckillFormRef.value.validate(async (valid) => {
        if (!valid) return

        seckillSubmitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            type: 'seckill',
            agent_id: agentId.value,
            store_id: storeId.value,
            ...seckillForm
          }

          if (seckillTimeRange.value && seckillTimeRange.value.length === 2) {
            payload.start_time = seckillTimeRange.value[0]
            payload.end_time = seckillTimeRange.value[1]
          }

          let response
          if (isEditSeckill.value) {
            payload.id = seckillForm.id
            response = await axios.put(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/activity_api.php`, payload)
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditSeckill.value ? '更新成功' : '创建成功')
            seckillDialogVisible.value = false
            fetchSeckill()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败')
        } finally {
          seckillSubmitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 查看活动关联商品
    const viewActivityProducts = async (activity) => {
      currentActivity.value = activity
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/activity_api.php`, {
          params: {
            action: 'activity_products',
            activity_id: activity.id,
            store_id: storeId.value
          }
        })
        if (response.data.code === 200) {
          activityProducts.value = response.data.data
          productDialogVisible.value = true
        }
      } catch (error) {
        console.error('获取关联商品失败:', error)
        ElMessage.error('获取数据失败')
      }
    }

    // 删除
    const handleDelete = async (id, type) => {
      const loadingInstance = ElLoading.service({ text: '删除中...' })
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/activity_api.php`, {
          data: { id, store_id: storeId.value, type }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          if (type === 'activity') fetchActivities()
          else if (type === 'promotion') fetchPromotions()
          else if (type === 'seckill') fetchSeckill()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error('删除失败')
      } finally {
        loadingInstance.close()
      }
    }

    onMounted(() => {
      fetchProducts()
      fetchCoupons()
      fetchActivities()
      fetchPromotions()
      fetchSeckill()
    })

    return {
      activeTab,
      defaultImage,
      // 主题活动
      activityLoading,
      activityList,
      stats,
      activitySearchKey,
      activityStatusFilter,
      activityPagination,
      // 营销活动
      promotionLoading,
      promotionList,
      promotionStatusFilter,
      promotionPagination,
      // 秒杀
      seckillLoading,
      seckillList,
      seckillStatusFilter,
      seckillPagination,
      // 商品和优惠券选项
      productOptions,
      couponOptions,
      featureOptions,
      // 活动弹窗
      activityDialogVisible,
      isEditActivity,
      activitySubmitting,
      activityFormRef,
      activityDateRange,
      activityCoverFiles,
      activityBgFiles,
      activityForm,
      activityRules,
      // 营销活动弹窗
      promotionDialogVisible,
      isEditPromotion,
      promotionSubmitting,
      promotionFormRef,
      promotionTimeRange,
      promotionForm,
      promotionRules,
      // 规则相关
      fullReductionRules,
      groupBuyRules,
      inviteRules,
      checkInRules,
      // 秒杀弹窗
      seckillDialogVisible,
      isEditSeckill,
      seckillSubmitting,
      seckillFormRef,
      seckillTimeRange,
      seckillForm,
      seckillRules,
      // 关联商品
      productDialogVisible,
      currentActivity,
      activityProducts,
      // 方法
      getActivityTypeText,
      getPromotionTypeText,
      getPromotionStatusText,
      getPromotionStatusType,
      getSeckillStatusText,
      getSeckillStatusType,
      formatDateTime,
      formatFullReductionText,
      resetActivitySearch,
      onPromotionTypeChange,
      addFullReductionRule,
      removeFullReductionRule,
      addContinuousReward,
      removeContinuousReward,
      openAddDialog,
      openEditDialog,
      submitActivity,
      openAddPromotionDialog,
      openEditPromotionDialog,
      submitPromotion,
      openAddSeckillDialog,
      openEditSeckillDialog,
      submitSeckill,
      viewActivityProducts,
      handleDelete,
      fetchActivities,
      fetchPromotions,
      fetchSeckill,
      handleCoverChange,
      handleBgChange
    }
  }
}
</script>

<style scoped lang="scss">
.activity-management {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;

    .stat-card {
      background: #fff;
      border-radius: 12px;
      padding: 16px 24px;
      text-align: center;
      min-width: 120px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      &.success .stat-value {
        color: #67c23a;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 700;
      }

      .stat-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-top: 4px;
      }
    }
  }

  .activity-tabs {
    :deep(.el-tabs__header) {
      margin-bottom: 20px;
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  .activity-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 20px;

    .activity-card {
      background: #fff;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transition: all 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      }

      .activity-cover {
        position: relative;
        height: 160px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .activity-status {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;

          &.status-active {
            background: rgba(103, 194, 58, 0.9);
            color: #fff;
          }

          &.status-ended {
            background: rgba(0, 0, 0, 0.6);
            color: #fff;
          }
        }
      }

      .activity-info {
        padding: 16px;

        .activity-name {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .activity-date {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #8c8c8c;
          margin: 8px 0;
        }

        .activity-desc {
          font-size: 13px;
          color: #666;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }

      .activity-actions {
        padding: 12px 16px;
        border-top: 1px solid #f0f0f0;
        display: flex;
        gap: 16px;
      }
    }
  }

  .product-info {
    display: flex;
    gap: 12px;
    align-items: center;

    .product-img {
      width: 50px;
      height: 50px;
      border-radius: 8px;
      object-fit: cover;
    }

    .product-detail {
      .product-name {
        font-weight: 500;
        margin-bottom: 4px;
      }

      .original-price {
        font-size: 12px;
        color: #8c8c8c;
        text-decoration: line-through;
      }
    }
  }


  // 规则表单样式（营销活动专用）
  .rule-section {
    background: #f5f7fa;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 20px;

    .rule-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e4e7ed;

      .el-icon {
        font-size: 18px;
        color: #409eff;
      }
    }

    .rule-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      background: #fff;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid #ebeef5;

      .rule-label {
        color: #606266;
        font-size: 13px;
        white-space: nowrap;
      }

      .rule-unit {
        color: #909399;
        font-size: 12px;
        margin-left: 4px;
      }
    }

    .full-reduction-rules,
    .group-buy-rules {
      .rule-item {
        flex-wrap: wrap;

        @media (min-width: 768px) {
          flex-wrap: nowrap;
        }
      }
    }

    .add-rule-btn {
      margin-top: 8px;
      margin-left: 0;
    }

    .rule-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 12px;
      padding: 8px 12px;
      background: #fff6e0;
      border-radius: 6px;
    }
  }

  // 邀请规则样式
  .reward-group {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }

    .reward-group-title {
      font-size: 13px;
      font-weight: 500;
      color: #409eff;
      margin-bottom: 12px;
      padding-left: 8px;
      border-left: 3px solid #409eff;
    }

    .reward-row {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      @media (min-width: 768px) {
        flex-wrap: nowrap;
      }
    }

    .reward-item {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      background: #fff;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid #ebeef5;

      .reward-label {
        font-size: 13px;
        color: #606266;
        min-width: 60px;
        white-space: nowrap;
      }

      .reward-unit {
        font-size: 12px;
        color: #909399;
        white-space: nowrap;
      }

      &.reward-item-select {
        .reward-label {
          min-width: 60px;
        }
      }
    }
  }

  // 连续签到奖励容器
  .continuous-rewards {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .rule-item {
      flex-wrap: wrap;

      @media (min-width: 768px) {
        flex-wrap: nowrap;
      }
    }
  }

  .seckill-price {
    font-size: 16px;
    font-weight: 600;
    color: #f56c6c;
  }

  .empty-state {
    grid-column: 1 / -1;
    padding: 60px 0;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }

  .upload-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 8px;
  }

  // 规则表单样式
  .rule-section {
    background: #f5f7fa;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 20px;

    .rule-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e4e7ed;

      .el-icon {
        font-size: 18px;
        color: #409eff;
      }
    }

    .full-reduction-rules {
      .rule-item {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
        flex-wrap: wrap;

        .rule-label {
          color: #666;
          font-size: 14px;
        }
      }
    }

    .reward-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    .rule-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 12px;
    }
  }

  .rule-preview {
    font-size: 12px;
    color: #666;
  }
}

// 响应式
@media (max-width: 768px) {
  .activity-management {
    .rule-section {

      .rule-item,
      .reward-item {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }
}
</style>